// Port of the search autocomplete dropdown from
// src/app/components/nav-bar/nav-bar.component.html (the #searchDropdown block:
// artworks list via mypart-search-list-item, loading skeletons via
// mypart-search-list-skeleton, the error-message / link-entered panel and the
// "Add it here" submit-song footer).
//
// NOTE(port): the Angular project has NO standalone search.component — this
// markup lives directly inside nav-bar.component.html. It is extracted here as
// a presentational component; ALL behavior (debounced lookups, keyboard
// handling, artwork selection flow) stays in NavBar.tsx exactly where the
// Angular component kept it. The markup is converted verbatim.
//
// (outsideClick) on #searchDropdown → useOutsideClick → onClose. The redundant
// second (outsideClick) Angular put on the inner .error-message-container
// (same callback, nested element) is intentionally not duplicated.

import type { RefObject } from 'react';
import type { Artwork } from '../../interfaces/artwork';
import SearchListItem from './SearchListItem';
import SearchListSkeleton from '../../shared/components/SearchListSkeleton';
import { useOutsideClick } from '../../shared/hooks/useOutsideClick';

// Angular: skeletonArray = Array.from({length: 3}, (_, i) => ({[i]: i}));
const skeletonArray = Array.from({ length: 3 }, (_, i) => i);

export interface SearchProps {
  /** navBarService.searchDropDownHidden() — applied as the `hidden` class. */
  hidden: boolean;
  showBigSearchHomePage: boolean | undefined;
  showNothingFound: boolean;
  isLoading: boolean;
  artworks: Artwork[];
  errorMessageFromPrompt: string | undefined;
  showLinkHasBeenEntered: boolean;
  /** NavBar's #searchDropdown ElementRef (used for scrollTo(0, 0)). */
  dropdownRef?: RefObject<HTMLDivElement | null>;
  onSelectArtwork: (artwork: Artwork) => void;
  onSubmitSong: () => void;
  /** closeSearchDropdownAndHideNothingFound() */
  onClose: () => void;
}

export default function Search({
  hidden,
  showBigSearchHomePage,
  showNothingFound,
  isLoading,
  artworks,
  errorMessageFromPrompt,
  showLinkHasBeenEntered,
  dropdownRef,
  onSelectArtwork,
  onSubmitSong,
  onClose
}: SearchProps) {
  const outsideClickRef = useOutsideClick<HTMLDivElement>(() => onClose());

  return (
    <div className="w-full relative z-50">
      <div ref={(el) => {
             outsideClickRef.current = el;
             if (dropdownRef) {
               dropdownRef.current = el;
             }
           }}
           onClick={onClose}
           className={`w-full z-10 h-auto max-h-[500px] absolute top-1 left-0 bg-gray-50
                        border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-gray-500
                        focus:border-blue-500 block dark:bg-dark_input-500 dark:border-gray-600 overflow-y-hidden
                        dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500${hidden ? ' hidden' : ''}${showBigSearchHomePage ? ' !max-h-[350px]' : ''}`}>
        {!showNothingFound && (
          <section className={`overflow-y-scroll max-h-[440px] pl-2.5 pr-0 sm:pr-2.5 py-2.5${showBigSearchHomePage ? ' !max-h-[340px]' : ''}`}>
            {artworks.map((artwork) => (
              <SearchListItem key={artwork.id}
                              artwork={artwork}
                              onClick={(e) => {
                                e.stopPropagation();
                                onSelectArtwork(artwork);
                              }} />
            ))}
          </section>
        )}
        {isLoading && artworks.length === 0 && !showNothingFound && (
          <section className="pl-2.5 pr-0 sm:pr-2.5 py-2.5">
            {skeletonArray.map((i) => (
              <SearchListSkeleton key={i} />
            ))}
          </section>
        )}
        {(errorMessageFromPrompt || showLinkHasBeenEntered) && (
          <div className="error-message-container pl-2.5 pr-0 sm:pr-2.5 py-2.5">
            {showLinkHasBeenEntered && (
              <p>
                Please enter a song title, artist name or descriptive text instead of a link.
              </p>
            )}
          </div>
        )}

        <div className="h-[50px] flex justify-center items-center sticky left-0 bottom-0 bg-gray-50 dark:bg-dark_input-500 rounded-lg
                          sm:flex sm:flex-row w-full sm:justify-center border-t border-t-gray-300 dark:border-t-gray-700">
          <p className="text-[18px]">
            Can’t find a song/artist you’re looking for?{' '}
            <a onClick={onSubmitSong}
               className="underline font-bold hover:text-mypart_red-500 w-[20%] cursor-pointer">Add it here</a>
          </p>
        </div>

      </div>
    </div>
  );
}
